/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BLOCK_RESTRICTED_COUNTRIES
 * Date:         2026-01-18
 * Modified By:  Cursor
 *
 * Description:
 * Add validation to reject blocked countries (Belarus, Cuba, Iran, Iraq, 
 * North Korea, Russia) from userCountry field. Validation uses .notOneOf()
 * to prevent selection of restricted countries.
 *
 * File Size Check:
 * - Current: 80 lines
 * - Adding: ~2 lines (import + validation rule)
 * - Projected: ~82 lines
 * - Decision: Proceed (within 150-line limit)
 *
 * Related Files:
 * - client/src/components/setup/user-profile/utils/userValidation.ts
 * - client/src/utils/countries.ts (BLOCKED_COUNTRIES export)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_VALIDATION_ADD_LOCATION
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Add validation for userCity, userState, userCountry fields.
 * userCountry is required (selected from dropdown), city and state are optional.
 *
 * File Size Check:
 * - Current: 51 lines
 * - Adding: ~10 lines
 * - Decision: Proceed (within 150-line limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/utils/userValidation.ts
 * - server/models/User.js (userCity, userState, userCountry schema)
 */
import * as Yup from "yup";
// [2026-01-18] BLOCK_RESTRICTED_COUNTRIES: Import blocked countries for validation
import { BLOCKED_COUNTRIES } from "../../../../utils/countries";

const normalizeUrl = (value?: string | null): string => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const userValidationSchema = Yup.object({
  firstName: Yup.string().min(2).required("First name is required"),
  lastName: Yup.string().min(2).required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  // [2026-02-13] FIX_PHONE_VALIDATION: Aligned with E.164 (max 15 digits incl. country code)
  phone: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "phone-format-if-present",
      "Please enter a valid international phone number",
      (value) => {
        if (!value || !value.trim()) return true;
        const digits = value.replace(/\D/g, "");
        if (digits.length < 7 || digits.length > 15) return false;
        return /^[+]?[1-9]\d{6,14}$/.test(value.trim());
      },
    ),
  // [2025-12-29] Figma alignment: User personal location validation
  userCity: Yup.string()
    .max(100, "City must be 100 characters or less"),
  userState: Yup.string()
    .max(100, "State must be 100 characters or less"),
  // [2026-01-18] BLOCK_RESTRICTED_COUNTRIES: Reject blocked countries from selection
  userCountry: Yup.string()
    .required("Country is required")
    .max(100, "Country must be 100 characters or less")
    .notOneOf(BLOCKED_COUNTRIES, "This country cannot be selected"),
  currency: Yup.string().oneOf(["USD", "EUR", "GBP"]).required("Select a currency"),
  jobTitle: Yup.string().max(80),
  bio: Yup.string().max(300, "Max 300 characters"),
  // [2026-02-06] Updated URL validation messages with example
  linkedIn: Yup.string()
    .transform((value) => normalizeUrl(value) || "")
    .url("Please enter a valid URL (e.g. https://www.linkedin.com/in/yourprofile)")
    .nullable()
    .notRequired(),
  x: Yup.string()
    .transform((value) => normalizeUrl(value) || "")
    .url("Please enter a valid URL (e.g. https://www.x.com/yourhandle)")
    .nullable()
    .notRequired(),
  instagram: Yup.string()
    .transform((value) => normalizeUrl(value) || "")
    .url("Please enter a valid URL (e.g. https://www.instagram.com/yourhandle)")
    .nullable()
    .notRequired(),
  facebook: Yup.string()
    .transform((value) => normalizeUrl(value) || "")
    .url("Please enter a valid URL (e.g. https://www.facebook.com/yourpage)")
    .nullable()
    .notRequired(),
});
